import React, { useEffect, useRef, useState } from 'react';
import { Button, Space, message } from 'antd';
import { CarOutlined, HomeOutlined, RightOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { GENERATE_SLA_SUMMARY_DOWNLOAD_REPORT, GenerateSlaSummaryDownloadReportVariables, GenerateSlaSummaryDownloadReportResponse } from './graphql/mutations/generateSlaSummaryDownloadReport';
import ParcelsDownloadModal from '../../parcels/components/ParcelsDownloadModal';
import { useDownloadCooldown } from '../../../../hooks/useDownloadCooldown';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import TablePaginationBar from '../../../../components/TablePaginationBar';
import HubSlaMonitoringFilter from './components/HubSlaMonitoringFilter';
import SlaMonitoringTable from './components/SlaMonitoringTable';
import SlaMonitoringPieChart from './components/SlaMonitoringPieChart';
import SlaDetailDrawer from './components/SlaDetailDrawer';
import BreakdownDrawer from './components/BreakdownDrawer';
import CentralParcelDetailDrawer from '../../../central/parcels/components/CentralParcelDetailDrawer';
import { getUserInfo } from '../../../../utils/auth';
import type { SlaMonitoringFilters as TableFilters } from './components/SlaMonitoringFilter';
import { useHubSlaMonitoring } from './hooks/useHubSlaMonitoring';
import {
    ARRIVAL_POINT_TYPE_WHS,
    ARRIVAL_POINT_TYPE_HUB,
    TIMEZONE_LOCAL,
    TIMEZONE_WEST_US,
} from './constants';
import type { HubSlaMonitoringFilters } from './components/HubSlaMonitoringFilter';
import { useWarehouseContext } from '../../../../contexts/WarehouseContext';
import { SiteType } from '../../../../graphql/types/SiteType';

const HubParcelSlaMonitoringPage: React.FC = () => {
    const { t } = useTranslation();
    const { currentWarehouse } = useWarehouseContext();
    const {
        filters,
        setFilters,
        whsData,
        dspData,
        viewMode,
        handleViewModeChange,
        summaryData,
        loading,
        handleDetailClick,
        handleAnomalyClick,
        drawerVisible,
        drawerTitle,
        drawerData,
        drawerColumns,
        setDrawerVisible,
        breakdownDrawerVisible,
        setBreakdownDrawerVisible,
        breakdownData,
        breakdownTitle,
        breakdownAnomalyType,
        breakdownLoading,
        breakdownPage,
        breakdownPageSize,
        breakdownTotal,
        breakdownTotalPages,
        handleDriverSideBreakdownClick,
        handleBreakdownPageChange,
        handleBreakdownPageSizeChange,
        handleBreakdownRowClick,
        detailDrawerDimensionOverride,
        detailFilters,
        setDetailFilters,
        areas,
        drivers,
        detailDrawerDsps,
        detailDrawerAreas,
        detailDrawerDrivers,
        drawerDrivers,
        detailPagination,
        handleDetailPageChange,
        detailSortConfig,
        handleDetailSortChange,
        detailLoading,
        lastDetailContext,
        tablePagination,
        handlePageChange,
        handleSortChange,
        dsps,
        warehouses,
        warehouseId,
        orderDetailVisible,
        orderId,
        setOrderDetailVisible,
        showOrderDetail,
        dataUpdatedAtText,
        sortConfig,
    } = useHubSlaMonitoring();

    const hubIdNumber = currentWarehouse?.siteType != null && Number(currentWarehouse.siteType) === SiteType.HUB && currentWarehouse?.id
        ? Number(currentWarehouse.id)
        : undefined;
    const hubId = hubIdNumber != null ? String(hubIdNumber) : '';

    const [downloadModalVisible, setDownloadModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState('');
    const { downloadLoading, setDownloadLoading, cooldownRemaining, getLastDownloadTime, setLastDownloadTime } = useDownloadCooldown(`hubSlaMonitoringDownloadLastTime_${getUserInfo()?.account?.id}`);

    const [generateReportMutation] = useMutation<
        GenerateSlaSummaryDownloadReportResponse,
        GenerateSlaSummaryDownloadReportVariables
    >(GENERATE_SLA_SUMMARY_DOWNLOAD_REPORT);

    const handleDownload = async () => {
        const currentTime = Date.now();
        const lastTime = getLastDownloadTime();

        if (currentTime - lastTime < 5000) {
            const remainingTime = Math.ceil((5000 - (currentTime - lastTime)) / 1000);
            message.warning(t('wms.delivery.dailyTaskMonitoring.messages.waitBeforeRetry', { remainingTime }));
            return;
        }
        if (downloadLoading) {
            message.warning(t('wms.delivery.dailyTaskMonitoring.messages.generatingInProgress'));
            return;
        }

        try {
            setDownloadLoading(true);
            setLastDownloadTime(currentTime);

            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const hasDateRange = filters.arrivalTimeDateRange?.[0] && filters.arrivalTimeDateRange?.[1];
            const arrivalDateStart = hasDateRange ? filters.arrivalTimeDateRange![0].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
            const arrivalDateEnd = hasDateRange ? filters.arrivalTimeDateRange![1].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
            const selectedDspIds = filters.selectedDsps && filters.selectedDsps.length > 0
                ? filters.selectedDsps.map((name: string) => dsps.find((d: any) => d.name === name)?.id).filter((id: number | undefined): id is number => id != null)
                : undefined;
            const selectedWarehouseIds = filters.selectedWarehouses && filters.selectedWarehouses.length > 0
                ? (filters.selectedWarehouses as string[]).map((id: string) => Number(id)).filter((n: number) => Number.isFinite(n) && n > 0)
                : warehouses.length > 0 ? warehouses.map(w => w.id) : undefined;
            const identifierCol = viewMode === 'dsp'
                ? { key: 'dsp', enabled: true, label: t('wms.delivery.parcelSlaMonitoring.columns.dsp') }
                : { key: 'warehouseName', enabled: true, label: t('wms.delivery.dailyTaskMonitoring.hubTabs.whsView') };
            const columnConfig = [
                identifierCol,
                { key: 'actualArrivals', enabled: true, label: t('wms.delivery.parcelSlaMonitoring.columns.actualArrivals') },
                { key: 'pickedCount', enabled: true, label: t('wms.delivery.parcelSlaMonitoring.columns.pickedCount') },
                { key: 'deliveredLatest', enabled: true, label: t('wms.delivery.parcelSlaMonitoring.columns.deliveredLatest') },
                { key: 'overallConversionRate', enabled: true, label: t('wms.delivery.parcelSlaMonitoring.columns.overallConversionRate') },
                { key: 'pickupRate', enabled: true, label: t('wms.delivery.parcelSlaMonitoring.columns.pickupRate') },
                { key: 'latestDeliveryRate', enabled: true, label: t('wms.delivery.parcelSlaMonitoring.columns.latestDeliverySuccessRate') },
                { key: 'unpickedCount', enabled: true, label: t('wms.delivery.parcelSlaMonitoring.columns.unpickedCount') },
                { key: 'inProgress', enabled: true, label: t('wms.delivery.parcelSlaMonitoring.columns.inProgress') },
                { key: 'failedIncomplete', enabled: true, label: t('wms.delivery.parcelSlaMonitoring.columns.failedNotReturned') },
                { key: 'failedReturnedToWhsCount', enabled: true, label: t('wms.delivery.parcelSlaMonitoring.columns.failedReturnedToWhsCount') },
                { key: 'returnedParcelCount', enabled: true, label: t('wms.delivery.parcelSlaMonitoring.columns.returnedParcelCount') },
                { key: 'lostDamagedCount', enabled: true, label: t('wms.delivery.parcelSlaMonitoring.columns.lostDamaged') }
            ];
            const response = await generateReportMutation({
                variables: {
                    arrivalDateStart,
                    arrivalDateEnd,
                    hubId: hubIdNumber,
                    warehouseIds: selectedWarehouseIds,
                    dspIds: selectedDspIds,
                    cityNames: filters.selectedCities && filters.selectedCities.length > 0 ? filters.selectedCities : undefined,
                    arrivalPointType: filters.arrivalPointType ?? ARRIVAL_POINT_TYPE_WHS,
                    clientTimezone: filters.timezone ?? TIMEZONE_LOCAL,
                    dimension: viewMode,
                    columnConfig,
                    timezone: userTimezone,
                    sortBy: sortConfig.sortBy,
                    sortDirection: sortConfig.sortDirection,
                }
            });
            const fileName = response.data?.generateSlaSummaryDownloadReport;
            if (fileName) {
                setGeneratedFileName(fileName);
                setDownloadModalVisible(true);
            } else {
                message.error(t('wms.delivery.dailyTaskMonitoring.messages.failedToStartGeneration'));
            }
        } catch (error: any) {
            let errorMessage = t('wms.delivery.dailyTaskMonitoring.messages.generationFailedTryLater');
            if (error?.graphQLErrors?.[0]) {
                errorMessage = error.graphQLErrors[0].extensions?.message || error.graphQLErrors[0].message || errorMessage;
            } else if (error?.message) {
                errorMessage = error.message;
            }
            message.error(errorMessage);
        } finally {
            setDownloadLoading(false);
        }
    };

    const [searchParams, setSearchParams] = useSearchParams();
    const skipNextStateToUrlRef = useRef(false);
    // URL -> 日期：从 URL 的 arrivalDateStart/arrivalDateEnd 同步到筛选器；应用后跳过下一次 state->URL，避免用未提交的 filters 覆盖用户改的 URL
    useEffect(() => {
        const start = searchParams.get('arrivalDateStart');
        const end = searchParams.get('arrivalDateEnd');
        if (!start || !end) return;
        const startDay = dayjs(start, 'YYYY-MM-DD', true);
        const endDay = dayjs(end, 'YYYY-MM-DD', true);
        if (!startDay.isValid() || !endDay.isValid() || startDay.isAfter(endDay)) return;
        const hasDateRange = filters.arrivalTimeDateRange?.[0] && filters.arrivalTimeDateRange?.[1];
        const currentStart = hasDateRange ? filters.arrivalTimeDateRange![0].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
        const currentEnd = hasDateRange ? filters.arrivalTimeDateRange![1].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
        if (currentStart === start && currentEnd === end) return;
        skipNextStateToUrlRef.current = true;
        setFilters({
            ...filters,
            arrivalTimeDateRange: [startDay.startOf('day'), endDay.startOf('day')],
            arrivalTimeDatePreset: 'custom',
            arrivalDate: endDay.startOf('day'),
            dateRange: [startDay.startOf('day'), endDay.startOf('day')],
            dateRangePreset: 'last3days'
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅随 URL 日期变化时应用，不依赖 filters 避免循环
    }, [searchParams.get('arrivalDateStart'), searchParams.get('arrivalDateEnd')]);

    // 树节点、日期变化时同步到 URL（仅当与当前 URL 不同时更新；若刚应用了 URL->日期则跳过一次，避免闪烁）
    const hasDateRange = filters.arrivalTimeDateRange?.[0] && filters.arrivalTimeDateRange?.[1];
    const arrivalDateStart = hasDateRange ? filters.arrivalTimeDateRange![0].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
    const arrivalDateEnd = hasDateRange ? filters.arrivalTimeDateRange![1].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
    useEffect(() => {
        if (skipNextStateToUrlRef.current) {
            skipNextStateToUrlRef.current = false;
            return;
        }
        const urlHub = searchParams.get('hubId') ?? '';
        const urlStart = searchParams.get('arrivalDateStart') ?? '';
        const urlEnd = searchParams.get('arrivalDateEnd') ?? '';
        if (urlHub === hubId && urlStart === arrivalDateStart && urlEnd === arrivalDateEnd) return;
        const next = new URLSearchParams(searchParams);
        if (hubId) {
            next.set('hubId', hubId);
        } else {
            next.delete('hubId');
        }
        next.set('arrivalDateStart', arrivalDateStart);
        next.set('arrivalDateEnd', arrivalDateEnd);
        setSearchParams(next, { replace: true });
    }, [hubId, arrivalDateStart, arrivalDateEnd]);

    const hubFilters: HubSlaMonitoringFilters = {
        arrivalPointType: filters.arrivalPointType,
        timezone: filters.timezone,
        arrivalDate: filters.arrivalDate,
        arrivalTimeDateRange: filters.arrivalTimeDateRange,
        arrivalTimeDatePreset: filters.arrivalTimeDatePreset,
        selectedWarehouses: filters.selectedWarehouses,
        selectedDsps: filters.selectedDsps,
        viewMode: filters.viewMode,
    };

    const currentData = viewMode === 'dsp' ? dspData : whsData;

    const getRateColor = (rate: number): string => {
        if (rate === 100) return 'text-green-500';
        if (rate >= 70) return 'text-blue-500';
        if (rate >= 30) return 'text-yellow-500';
        return 'text-red-500';
    };

    const getBadMetricRateColor = (rate: number): string => {
        const r = Number.isFinite(rate) ? Math.max(0, rate) : 0;
        if (r <= 0) return 'text-green-500';
        if (r < 10) return 'text-blue-500';
        if (r < 30) return 'text-yellow-500';
        return 'text-red-500';
    };

    const renderClickableNumber = (
        value: number,
        onClick?: () => void,
        sizeClass: string = 'text-2xl'
    ) => {
        if (value <= 0) {
            return <span className={`${sizeClass} text-gray-800 font-normal whitespace-nowrap`}>{value}</span>;
        }
        if (onClick) {
            return (
                <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        onClick();
                    }}
                    style={{ color: '#1890ff' }}
                    className={`${sizeClass} font-normal whitespace-nowrap`}
                >
                    {value}
                </a>
            );
        }
        return <span className={`${sizeClass} text-gray-800 font-normal whitespace-nowrap`}>{value}</span>;
    };

    const renderClickablePercent = (value: number, onClick: () => void, className: string) => {
        const safe = Number.isFinite(value) ? value : 0;
        if (safe <= 0) return <span className={className}>{safe.toFixed(2)}%</span>;
        return (
            <a
                href="#"
                onClick={(e) => {
                    e.preventDefault();
                    onClick();
                }}
                style={{ color: '#1890ff' }}
                className={className}
            >
                {safe.toFixed(2)}%
            </a>
        );
    };

    const handleHubFiltersChange = (newF: HubSlaMonitoringFilters) => {
        setFilters({ ...filters, ...newF });
    };

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            <HubSlaMonitoringFilter
                filters={hubFilters}
                onFiltersChange={handleHubFiltersChange}
                dsps={dsps}
                warehouses={warehouses}
            />

            {/* 最小宽度保证小屏可读，大屏自适应填满 */}
            <div className="overflow-x-auto pb-2 -mx-3 px-3 -mt-2">
                <div className="grid grid-cols-3 gap-2 mb-6 w-full min-w-[720px]">
                    <div className="relative z-10 flex flex-col gap-2 border-l-4 border-purple-600 pl-2.5 min-w-0 self-start">
                        <p className="text-lg font-medium bg-red-500 text-white px-1.7 py-1 rounded text-center">
                        {t('wms.delivery.parcelSlaMonitoring.title')}
                    </p>
                    <div className="grid grid-cols-3 -mt-2 gap-x-[0.25rem] gap-y-0 lg:gap-x-4 items-start content-start" style={{ gridTemplateRows: 'auto minmax(1.5rem, auto) auto' }}>
                        <div className="flex flex-col gap-1 items-center min-w-0 rounded-lg">
                            <div className="flex items-center w-full gap-1">
                                <div className="min-w-0 flex-1 flex justify-center overflow-visible">
                                    {renderClickableNumber(
                                        summaryData?.parcelsSummary?.actualArrivals ?? 0,
                                        () => handleAnomalyClick('actualArrivals'),
                                        'text-2xl'
                                    )}
                                </div>
                                <span className="relative z-10 flex-shrink-0 text-gray-400 text-2xl leading-none w-6 flex items-center justify-center" aria-hidden><RightOutlined /></span>
                            </div>
                            <div className="flex items-center w-full gap-1">
                                <div className="min-w-0 flex-1 flex justify-center"><p className="text-xs text-gray-400 font-normal whitespace-nowrap">{t('wms.delivery.parcelSlaMonitoring.columns.actualArrivals')}</p></div>
                                <span className="flex-shrink-0 w-6" aria-hidden />
                            </div>
                            <div className="flex flex-col gap-1 items-center mt-5 w-full min-w-0">
                                {(() => {
                                    const conversionRate =
                                        summaryData?.parcelsSummary?.actualArrivals && summaryData?.parcelsSummary?.actualArrivals > 0
                                            ? ((summaryData.parcelsSummary.deliveredLatest || 0) /
                                                summaryData.parcelsSummary.actualArrivals) * 100
                                            : 0;
                                    return (
                                        <span className={`text-2xl ${getRateColor(parseFloat(conversionRate.toFixed(2)))} block text-center w-full font-normal`}>
                                            {conversionRate.toFixed(2)}%
                                        </span>
                                    );
                                })()}
                                <div className="flex flex-col items-center w-full min-w-0">
                                    <span className="text-xs text-gray-400 block text-center w-full break-words min-w-0">{t('wms.delivery.parcelSlaMonitoring.columns.overallConversionRate')}</span>
                                    <span className="text-xs text-gray-400 block text-center w-full break-words min-w-0">{t('wms.delivery.parcelSlaMonitoring.overallConversionRateFormula')}</span>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col gap-1 items-center min-w-0 rounded-lg">
                            <div className="flex items-center w-full gap-1">
                                <div className="min-w-0 flex-1 flex justify-center overflow-visible">
                                    {renderClickableNumber(
                                        summaryData?.parcelsSummary?.pickedCount ?? 0,
                                        () => handleAnomalyClick('picked'),
                                        'text-2xl'
                                    )}
                                </div>
                                <span className="relative z-10 flex-shrink-0 text-gray-400 text-sm leading-none w-6 flex items-center justify-center" aria-hidden><RightOutlined /></span>
                            </div>
                            <div className="flex items-center w-full gap-1">
                                <div className="min-w-0 flex-1 flex justify-center"><p className="text-xs text-gray-400 font-normal whitespace-nowrap">{t('wms.delivery.parcelSlaMonitoring.columns.pickedCount')}</p></div>
                                <span className="flex-shrink-0 w-6" aria-hidden />
                            </div>
                            <div className="flex flex-col gap-1 items-center mt-5 w-full min-w-0">
                                {(() => {
                                    const actualArrivals = summaryData?.parcelsSummary?.actualArrivals ?? 0;
                                    const pickedCount = summaryData?.parcelsSummary?.pickedCount ?? 0;
                                    const pickupRate = actualArrivals > 0 ? (pickedCount / actualArrivals) * 100 : 0;
                                    return (
                                        <span className={`text-2xl ${getRateColor(parseFloat(pickupRate.toFixed(2)))} block text-center w-full font-normal`}>
                                            {pickupRate.toFixed(2)}%
                                        </span>
                                    );
                                })()}
                                <div className="flex flex-col items-center w-full min-w-0">
                                    <span className="text-xs text-gray-400 block text-center w-full break-words min-w-0">
                                        {t('wms.delivery.parcelSlaMonitoring.columns.pickupRate')}
                                    </span>
                                    <span className="text-xs text-gray-400 block text-center w-full break-words min-w-0">
                                        {t('wms.delivery.parcelSlaMonitoring.pickupRateFormula')}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col gap-1 items-center min-w-0 rounded-lg">
                            <div className="relative w-full">
                                <div className="flex items-center justify-center gap-1">
                                    {renderClickableNumber(
                                        summaryData?.parcelsSummary?.deliveredLatest ?? 0,
                                        () => handleAnomalyClick('deliveredLatest'),
                                        'text-2xl'
                                    )}
                                </div>
                            </div>
                            <p className="text-xs text-gray-400 font-normal text-center w-full whitespace-nowrap">{t('wms.delivery.parcelSlaMonitoring.columns.deliveredLatest')}</p>
                            <div className="flex flex-col gap-1 items-center mt-5 w-full min-w-0">
                                {(() => {
                                    const pickedCount = summaryData?.parcelsSummary?.pickedCount ?? 0;
                                    const deliveredLatest = summaryData?.parcelsSummary?.deliveredLatest ?? 0;
                                    const deliveryRate = pickedCount > 0 ? (deliveredLatest / pickedCount) * 100 : 0;
                                    return (
                                        <span className={`text-2xl ${getRateColor(parseFloat(deliveryRate.toFixed(2)))} block text-center w-full font-normal`}>
                                            {deliveryRate.toFixed(2)}%
                                        </span>
                                    );
                                })()}
                                <div className="flex flex-col items-center w-full min-w-0">
                                    <span className="text-xs text-gray-400 block text-center w-full break-words min-w-0">
                                        {t('wms.delivery.dailyTaskMonitoring.summary.deliveryRate')}
                                    </span>
                                    <span className="text-xs text-gray-400 block text-center w-full break-words min-w-0">
                                        {t('wms.delivery.parcelSlaMonitoring.deliveryRateFormula')}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col gap-1 items-center mt-5 w-full min-w-0">
                            {renderClickableNumber(
                                summaryData?.parcelsSummary?.missScanWhsArrivalCount ?? 0,
                                () => handleAnomalyClick('missScanWhsArrival'),
                                'text-lg'
                            )}
                            <span className="text-xs text-gray-400 block text-center w-full break-words min-w-0">{t('wms.delivery.parcelSlaMonitoring.unscannedWhsArrivalScan')}</span>
                        </div>
                        {(() => {
                            const anomalyData = summaryData?.anomalyData;
                            const toNum = (v: unknown) => (typeof v === 'number' && !Number.isNaN(v) ? v : Number(v) || 0);
                            const unpicked12h = toNum(anomalyData?.overtime?.unpicked12h);
                            return (
                                <div className="flex flex-col gap-1 items-center mt-5 w-full min-w-0">
                                    {renderClickableNumber(unpicked12h, () => handleAnomalyClick('unpicked12h'), 'text-lg')}
                                    <span className="text-xs text-gray-400 block text-center w-full break-words min-w-0">
                                        {t('wms.delivery.parcelSlaMonitoring.unpickedOver12h')}
                                    </span>
                                </div>
                            );
                        })()}
                        {(() => {
                            const anomalyData = summaryData?.anomalyData;
                            const toNum = (v: unknown) => (typeof v === 'number' && !Number.isNaN(v) ? v : Number(v) || 0);
                            const undelivered24h = toNum(anomalyData?.overtime?.undelivered24h);
                            return (
                                <div className="flex flex-col gap-1 items-center mt-5 w-full min-w-0">
                                    {renderClickableNumber(undelivered24h, () => handleAnomalyClick('undelivered24h'), 'text-lg')}
                                    <span className="text-xs text-gray-400 block text-center w-full break-words min-w-0">
                                        {t('wms.delivery.parcelSlaMonitoring.undeliveredOver24hAfterPickup')}
                                    </span>
                                </div>
                            );
                        })()}
                        <div className="flex flex-col gap-1 items-center mt-5 w-full min-w-0">
                            {(() => {
                                const r = parseFloat(summaryData?.parcelsSummary?.returnedParcelIncompleteOver72hRate ?? '0');
                                const rateValue = parseFloat(r.toFixed(2));
                                return (
                                    <>
                                        {renderClickablePercent(
                                            rateValue,
                                            () => handleAnomalyClick('returnIncomplete72h'),
                                            `text-2xl ${getBadMetricRateColor(rateValue)} block text-center w-full font-normal`
                                        )}
                                        <span className="text-xs text-gray-400 block text-center w-full break-words min-w-0">
                                            {t('wms.delivery.parcelSlaMonitoring.returnedParcelIncompleteOver72hRate')}
                                        </span>
                                    </>
                                );
                            })()}
                        </div>
                        <div className="flex flex-col gap-1 items-center mt-5 w-full min-w-0">
                            {(() => {
                                const r = parseFloat(summaryData?.parcelsSummary?.failedReturnedToWhsUnprocessedOver24hRate ?? '0');
                                const rateValue = parseFloat(r.toFixed(2));
                                return (
                                    <>
                                        {renderClickablePercent(
                                            rateValue,
                                            () => handleAnomalyClick('returnWarehouse24hUnprocessed'),
                                            `text-2xl ${getBadMetricRateColor(rateValue)} block text-center w-full font-normal`
                                        )}
                                        <span className="text-xs text-gray-400 block text-center w-full break-words min-w-0">
                                            {t('wms.delivery.parcelSlaMonitoring.failedReturnedToWhsUnprocessedOver24hRate')}
                                        </span>
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                </div>
                    <div className="flex flex-col gap-2 pr-6 min-w-0">
                        <div className="flex items-start justify-center p-0 w-fit max-w-full mx-auto mt-0">
                            <SlaMonitoringPieChart
                            data={
                                summaryData?.pieChartData
                                    ? [
                                        { name: t('wms.delivery.parcelSlaMonitoring.pieChart.unpicked'), value: summaryData.pieChartData.unpicked, type: 'unpicked' },
                                        { name: t('wms.delivery.parcelSlaMonitoring.pieChart.failedNotReturned'), value: summaryData.pieChartData.failedNotReturned, type: 'failedNotReturned' },
                                        { name: t('wms.delivery.parcelSlaMonitoring.pieChart.failedReturnedToWhs'), value: summaryData.pieChartData.failedReturnedToWhs, type: 'failedReturnedToWhs' },
                                        { name: t('wms.delivery.parcelSlaMonitoring.pieChart.inDelivery'), value: summaryData.pieChartData.inDelivery, type: 'inDelivery' },
                                        { name: t('wms.delivery.parcelSlaMonitoring.pieChart.returned'), value: summaryData.pieChartData.returnedParcel, type: 'returnedParcel' },
                                        { name: t('wms.delivery.parcelSlaMonitoring.pieChart.lostDamaged'), value: summaryData.pieChartData.lostDamaged, type: 'lostDamaged' },
                                    ]
                                    : undefined
                            }
                            onSliceClick={(type) => handleAnomalyClick(type)}
                        />
                        </div>
                    </div>
                    <div className="relative z-10 flex flex-col gap-4 min-w-0 ml-6 sm:ml-8">
                    {(() => {
                        const anomalyData = summaryData?.anomalyData;
                        const toNum = (v: unknown) => (typeof v === 'number' && !Number.isNaN(v) ? v : Number(v) || 0);
                        const aboutToBreakTotal = toNum(anomalyData?.aboutToBreak?.total48To72h);
                        const aboutToBreakWarehouse = toNum(anomalyData?.aboutToBreak?.warehouseSide48To72h);
                        const aboutToBreakDriver = toNum(anomalyData?.aboutToBreak?.driverSide48To72h);
                        const alreadyBreakTotal = toNum(anomalyData?.alreadyBreak?.totalOver72h);
                        const alreadyBreakWarehouse = toNum(anomalyData?.alreadyBreak?.warehouseSideOver72h);
                        const alreadyBreakDriver = toNum(anomalyData?.alreadyBreak?.driverSideOver72h);
                        const renderNum = (displayNum: number, anomalyType: string) =>
                            renderClickableNumber(displayNum, () => handleAnomalyClick(anomalyType), 'text-2xl');
                        return (
                            <div
                                className="grid min-w-max w-max"
                                style={{
                                    gridTemplateColumns: '1rem minmax(0, max-content) 1rem minmax(0, max-content) 0.75rem minmax(0, max-content)',
                                    gridTemplateRows: 'auto auto auto auto',
                                    columnGap: 'clamp(0.2rem, 1vw, 1rem)',
                                    rowGap: 'clamp(0.75rem, 1vw + 0.5rem, 1rem)',
                                }}
                            >
                                <div className="col-start-1 row-start-1 row-span-2 border-l-4 border-purple-600 self-stretch min-h-[4rem]" style={{ paddingLeft: 'clamp(0.25rem, 1vw + 0.25rem, 1rem)' }} aria-hidden />
                                <p className="col-start-2 col-span-5 row-start-1 text-lg font-bold text-amber-500 pt-0">{t('wms.delivery.parcelSlaMonitoring.aboutToBreak')}</p>
                                <div className="col-start-2 col-span-1 row-start-2 flex flex-col items-center min-w-0">
                                    {renderNum(aboutToBreakTotal, 'noUpdate48To72h')}
                                    <span className="text-xs text-gray-400 text-center break-words">{t('wms.delivery.parcelSlaMonitoring.break48to72h')}</span>
                                </div>
                                <div className="col-start-3 row-start-2 flex flex-col gap-1 items-center justify-start"><span className="text-2xl font-semibold text-gray-500 leading-none">=</span></div>
                                <div className="col-start-4 row-start-2 flex flex-col items-center min-w-0">
                                    {renderNum(aboutToBreakWarehouse, 'noUpdate48To72hWarehouse')}
                                    <span className="text-xs text-gray-400 text-center">{t('wms.delivery.parcelSlaMonitoring.warehouseSide')}</span>
                                </div>
                                <div className="col-start-5 row-start-2 flex flex-col gap-1 items-center justify-start"><span className="text-2xl font-semibold text-gray-500 leading-none">+</span></div>
                                <div className="col-start-6 row-start-2 flex flex-col items-center min-w-0">
                                    {renderClickableNumber(aboutToBreakDriver, () => { void handleDriverSideBreakdownClick('noUpdate48To72h'); }, 'text-2xl')}
                                    <span className="text-xs text-gray-400 text-center">{t('wms.delivery.parcelSlaMonitoring.driverSide')}</span>
                                </div>
                                <div className="col-start-1 row-start-3 row-span-2 border-l-4 border-purple-600 self-stretch min-h-[4rem]" style={{ paddingLeft: 'clamp(0.25rem, 1vw + 0.25rem, 1rem)' }} aria-hidden />
                                <p className="col-start-2 col-span-5 row-start-3 text-lg font-bold text-red-600 pt-2">{t('wms.delivery.parcelSlaMonitoring.alreadyBreak')}</p>
                                <div className="col-start-2 col-span-1 row-start-4 flex flex-col items-center min-w-0">
                                    {renderNum(alreadyBreakTotal, 'noUpdateOver72h')}
                                    <span className="text-xs text-gray-400 text-center break-words">{t('wms.delivery.parcelSlaMonitoring.break72h')}</span>
                                </div>
                                <div className="col-start-3 row-start-4 flex flex-col gap-1 items-center justify-start"><span className="text-2xl font-semibold text-gray-500 leading-none">=</span></div>
                                <div className="col-start-4 row-start-4 flex flex-col items-center min-w-0">
                                    {renderNum(alreadyBreakWarehouse, 'noUpdateOver72hWarehouse')}
                                    <span className="text-xs text-gray-400 text-center">{t('wms.delivery.parcelSlaMonitoring.warehouseSide')}</span>
                                </div>
                                <div className="col-start-5 row-start-4 flex flex-col gap-1 items-center justify-start"><span className="text-2xl font-semibold text-gray-500 leading-none">+</span></div>
                                <div className="col-start-6 row-start-4 flex flex-col items-center min-w-0">
                                    {renderClickableNumber(alreadyBreakDriver, () => { void handleDriverSideBreakdownClick('noUpdateOver72h'); }, 'text-2xl')}
                                    <span className="text-xs text-gray-400 text-center">{t('wms.delivery.parcelSlaMonitoring.driverSide')}</span>
                                </div>
                            </div>
                        );
                    })()}
                    </div>
                </div>
            </div>

            <div className="mb-4">
                <div className="flex items-center gap-3">
                    <Space>
                        <Button
                            type={viewMode === 'dsp' ? 'primary' : 'default'}
                            icon={<CarOutlined />}
                            onClick={() => handleViewModeChange('dsp')}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('wms.delivery.dailyTaskMonitoring.hubTabs.dspView')}</span>
                        </Button>
                        <Button
                            type={viewMode === 'whs' ? 'primary' : 'default'}
                            icon={<HomeOutlined />}
                            onClick={() => handleViewModeChange('whs')}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('wms.delivery.dailyTaskMonitoring.hubTabs.whsView')}</span>
                        </Button>
                    </Space>
                    <span className="text-sm">
                        {dataUpdatedAtText && (
                            <span className="text-red-500 font-bold">{t('wms.delivery.dailyTaskMonitoring.dataUpdatedAt')} {dataUpdatedAtText} </span>
                        )}
                        <span className="text-black">{t('wms.delivery.parcelSlaMonitoring.dataUpdateLagNote')}</span>
                    </span>
                    <div className="ml-auto">
                        <Button
                            type="primary"
                            onClick={handleDownload}
                            loading={cooldownRemaining > 0}
                            disabled={downloadLoading || cooldownRemaining > 0}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">
                                {cooldownRemaining > 0
                                    ? `${t('components.button.download')} ${cooldownRemaining}s`
                                    : t('components.button.download')
                                }
                            </span>
                        </Button>
                    </div>
                </div>
            </div>

            {/* 口径说明（与时区、类型下拉框联动） */}
            <div className="mb-4">
                <div
                    className="p-3 rounded"
                    style={{
                        backgroundColor: '#fff2f0',
                        border: '1px solid #ffccc7',
                        color: '#ff4d4f'
                    }}
                >
                    <div className="text-sm font-bold">
                        <strong>
                            {t('wms.delivery.parcelSlaMonitoring.metricDefinition', {
                                timezone: (filters.timezone ?? TIMEZONE_LOCAL) === TIMEZONE_WEST_US
                                    ? t('wms.delivery.parcelSlaMonitoring.columns.timezoneWestUS')
                                    : t('wms.delivery.parcelSlaMonitoring.columns.timezoneLocal'),
                                arrivalType: (filters.arrivalPointType ?? ARRIVAL_POINT_TYPE_WHS) === ARRIVAL_POINT_TYPE_HUB
                                    ? t('wms.delivery.parcelSlaMonitoring.metricDefinitionArrivalTypeHub')
                                    : t('wms.delivery.parcelSlaMonitoring.metricDefinitionArrivalTypeWhs')
                            })}
                        </strong>
                    </div>
                </div>
            </div>

            <SlaMonitoringTable
                data={currentData}
                viewMode={viewMode}
                onDetailClick={handleDetailClick}
                onSortChange={handleSortChange}
                loading={loading}
                pagination={tablePagination}
                filters={{ ...filters, selectedAreas: [], selectedDrivers: [] } as unknown as TableFilters}
                hideT1ToT5Rates
            />

            {tablePagination && (
                <TablePaginationBar
                    current={tablePagination.page}
                    pageSize={tablePagination.size}
                    total={tablePagination.totalElements}
                    onChange={handlePageChange}
                />
            )}

            <BreakdownDrawer
                visible={breakdownDrawerVisible}
                title={breakdownTitle}
                data={breakdownData}
                loading={breakdownLoading}
                anomalyType={breakdownAnomalyType}
                total={breakdownTotal}
                page={breakdownPage}
                pageSize={breakdownPageSize}
                totalPages={breakdownTotalPages}
                onPageChange={handleBreakdownPageChange}
                onPageSizeChange={handleBreakdownPageSizeChange}
                onClose={() => setBreakdownDrawerVisible(false)}
                onCountClick={handleBreakdownRowClick}
            />

            <SlaDetailDrawer
                visible={drawerVisible}
                title={drawerTitle}
                data={drawerData}
                columns={drawerColumns}
                loading={detailLoading}
                onClose={() => setDrawerVisible(false)}
                onOpenParcelDetail={showOrderDetail}
                onSortChange={handleDetailSortChange}
                currentSortBy={detailSortConfig.sortBy}
                currentSortDirection={detailSortConfig.sortDirection}
                pagination={detailPagination ? { page: detailPagination.page, size: detailPagination.size, total: detailPagination.total } : undefined}
                onPageChange={handleDetailPageChange}
                detailFilters={detailFilters}
                onDetailFiltersChange={setDetailFilters}
                dsps={detailDrawerDsps}
                areas={detailDrawerAreas}
                drivers={drawerDrivers}
                arrivalDateStart={arrivalDateStart}
                arrivalDateEnd={arrivalDateEnd}
                warehouseId={warehouseId}
                arrivalPointType={filters.arrivalPointType ?? ARRIVAL_POINT_TYPE_WHS}
                timezone={filters.timezone}
                selectedCities={filters.selectedCities?.length ? filters.selectedCities : undefined}
                statusField={lastDetailContext?.statusField ?? (lastDetailContext?.type ? (() => {
                    const type = lastDetailContext.type;
                    const anomalyStatusMap: { [key: string]: string } = {
                        'unpicked24h': 'isUnpicked24h',
                        'undelivered24h': 'isUndelivered24h',
                        'undelivered48h': 'isUndelivered48h',
                        'undelivered72h': 'isUndelivered72h',
                        'aboutToTimeout24h': 'isAboutToTimeout24h',
                        'aboutToTimeout48h': 'isAboutToTimeout48h',
                        'lostDamaged': 'isLostDamaged',
                        'noUpdate24h': 'isNoUpdate24h',
                        'noUpdate48To72h': 'isNoUpdate48To72h',
                        'noUpdate48To72hWarehouse': 'isNoUpdate48To72hWarehouse',
                        'noUpdate48To72hDriver': 'isNoUpdate48To72hDriver',
                        'noUpdateOver72h': 'isNoUpdateOver72h',
                        'noUpdateOver72hWarehouse': 'isNoUpdateOver72hWarehouse',
                        'noUpdateOver72hDriver': 'isNoUpdateOver72hDriver'
                    };
                    if (anomalyStatusMap[type]) return anomalyStatusMap[type];
                    const normalStatusMap: { [key: string]: string } = {
                        'actual_arrivals': 'isActualArrivals',
                        'in_progress': 'isInDelivery',
                        'in_delivery': 'isInDelivery',
                        'delivered_latest': 'isDeliveredLatest',
                        'returned_damaged_lost': 'isReturnedDamagedLost',
                        'failed_incomplete': 'isFailedIncomplete'
                    };
                    return normalStatusMap[type] || undefined;
                })() : undefined)}
                lastDetailContext={lastDetailContext}
                detailDimension={detailDrawerDimensionOverride ?? viewMode}
                warehouses={warehouses}
                hubId={hubIdNumber}
            />

            {/* 订单详情抽屉：明细内点击运单号打开（与 hub-daily-task-monitoring 一致） */}
            <CentralParcelDetailDrawer
                showInterceptionExemptionAlert={true}
                visible={orderDetailVisible}
                orderId={orderId}
                userRole={getUserInfo()?.account?.role?.name || ''}
                onClose={() => setOrderDetailVisible(false)}
            />

            <ParcelsDownloadModal
                visible={downloadModalVisible}
                onClose={() => setDownloadModalVisible(false)}
                fileName={generatedFileName}
            />
        </div>
    );
};

export default HubParcelSlaMonitoringPage;
